import React, { useState } from 'react';
import { ShoppingCart, User, Heart, Search, Leaf, Award, Sun, Play } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const Template17 = () => {
    const navigate = useNavigate();
    const [activeFilter, setActiveFilter] = useState('all');

    return (
        <div className="w-full min-h-screen bg-stone-50 font-sans">
            {/* Warm Nordic Header */}
                        {/* Header with Navigation */}
            <header className="bg-white border-b border-gray-200 sticky top-0 z-50 shadow-sm">
                <div className="container mx-auto px-4 lg:px-8">
                    <div className="flex justify-between items-center py-4 lg:py-5">
                        <h1 onClick={() => navigate('/template17')} className="text-2xl lg:text-3xl font-bold text-gray-900 cursor-pointer tracking-tight">
                            CURATED
                        </h1>
                        <div className="flex items-center gap-4 lg:gap-6">
                            <Search className="w-5 h-5 text-gray-900 cursor-pointer hover:text-gray-600 transition-colors" />
                            <Heart className="w-5 h-5 text-gray-900 cursor-pointer hover:text-gray-600 transition-colors" />
                            <div className="relative cursor-pointer" onClick={() => navigate('/template17/cart')}>
                                <ShoppingCart className="w-5 h-5 text-gray-900 hover:text-gray-600 transition-colors" />
                                <span className="absolute -top-2 -right-2 bg-gray-900 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center font-medium">0</span>
                            </div>
                            <User className="w-5 h-5 text-gray-900 cursor-pointer hover:text-gray-600 transition-colors" onClick={() => navigate('/template17/profile')} />
                        </div>
                    </div>
                    <nav className="border-t border-gray-200">
                        <div className="flex justify-center items-center gap-8 lg:gap-12 py-4">
                            <button onClick={() => navigate('/template17')} className="text-sm lg:text-base font-medium text-gray-900 hover:text-gray-600 transition-colors uppercase tracking-wide">Home</button>
                            <button onClick={() => navigate('/template17/shop')} className="text-sm lg:text-base font-medium text-gray-900 hover:text-gray-600 transition-colors uppercase tracking-wide">Shop</button>
                            <button onClick={() => navigate('/template17/contact')} className="text-sm lg:text-base font-medium text-gray-900 hover:text-gray-600 transition-colors uppercase tracking-wide">Contact</button>
                            <button onClick={() => navigate('/template17/orders')} className="text-sm lg:text-base font-medium text-gray-900 hover:text-gray-600 transition-colors uppercase tracking-wide">Orders</button>
                        </div>
                    </nav>
                </div>
            </header>

            {/* Human-Centered Hero */}
            <section className="px-6 lg:px-12 py-16 lg:py-24 bg-gradient-to-br from-stone-50 via-amber-50/30 to-stone-100">
                <div className="max-w-7xl mx-auto">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">
                        <div className="lg:col-span-6 space-y-8">
                            <div className="space-y-6">
                                <div className="flex items-center gap-3 text-stone-600">
                                    <Leaf size={20} strokeWidth={1.5} />
                                    <span className="text-sm font-light tracking-wide">Sustainably crafted</span>
                                </div>
                                <h2 className="text-4xl lg:text-5xl xl:text-6xl font-light text-stone-800 leading-tight">
                                    Natural beauty,<br/>
                                    <span className="text-amber-700">thoughtful design</span>
                                </h2>
                                <p className="text-lg lg:text-xl text-stone-600 font-light leading-relaxed max-w-lg">
                                    Discover collections inspired by Nordic simplicity.
                                    Each piece celebrates natural materials, ethical craftsmanship,
                                    and timeless functionality.
                                </p>
                            </div>

                            <div className="flex flex-wrap gap-4">
                                <button className="group flex items-center gap-3 px-8 py-4 bg-stone-800 text-stone-50 font-normal transition-all duration-300 hover:bg-stone-900 hover:shadow-lg">
                                    Explore Collection
                                    <div className="w-2 h-2 bg-amber-400 rounded-full group-hover:bg-amber-300 transition-colors"></div>
                                </button>
                                <button className="flex items-center gap-3 px-8 py-4 border border-stone-300 text-stone-700 font-normal hover:border-stone-800 transition-all duration-300">
                                    Our Story
                                </button>
                            </div>

                            {/* Human Values */}
                            <div className="grid grid-cols-3 gap-6 pt-6">
                                {[
                                    { icon: Leaf, label: "Sustainable", desc: "100% ethical" },
                                    { icon: Award, label: "Quality", desc: "Nordic crafted" },
                                    { icon: Sun, label: "Human", desc: "People first" }
                                ].map((value, index) => (
                                    <div key={index} className="text-center space-y-2">
                                        <value.icon className="mx-auto text-amber-600" size={24} strokeWidth={1.5} />
                                        <div className="text-sm font-medium text-stone-800">{value.label}</div>
                                        <div className="text-xs text-stone-500">{value.desc}</div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="lg:col-span-6">
                            <div className="relative">
                                <img
                                    src="https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=800&h=600&fit=crop"
                                    alt="Nordic Collection"
                                    className="w-full rounded-3xl shadow-xl"
                                />
                                <div className="absolute inset-0 bg-amber-100/20 rounded-3xl"></div>
                                <div className="absolute -bottom-4 -right-4 w-24 h-24 bg-amber-200/60 rounded-full blur-xl"></div>
                                <div className="absolute -top-4 -left-4 w-32 h-32 bg-stone-200/60 rounded-full blur-xl"></div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Natural Category Sections */}
            <section className="px-6 lg:px-12 py-16 lg:py-20 bg-white">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-12 lg:mb-16">
                        <h3 className="text-2xl lg:text-3xl font-light text-stone-800 mb-4">Thoughtfully curated</h3>
                        <p className="text-stone-600 font-light max-w-2xl mx-auto">
                            Collections designed with care for you, your family, and our shared environment
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
                        {[
                            { title: "Women", count: "280+ pieces", image: "https://images.unsplash.com/photo-1515372039744-b8f02a3ae446?w=500&h=600&fit=crop", accent: "bg-amber-100 text-amber-800" },
                            { title: "Men", count: "220+ pieces", image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=500&h=600&fit=crop", accent: "bg-stone-200 text-stone-800" },
                            { title: "Kids", count: "140+ pieces", image: "https://images.unsplash.com/photo-1518831959646-742c3a14ebf7?w=500&h=600&fit=crop", accent: "bg-rose-100 text-rose-800" },
                            { title: "Home", count: "95+ pieces", image: "https://images.unsplash.com/photo-1586023492125-27b2c045efd7?w=500&h=600&fit=crop", accent: "bg-green-100 text-green-800" }
                        ].map((category, index) => (
                            <div key={index} className="group cursor-pointer">
                                <div className="relative aspect-[4/5] overflow-hidden rounded-2xl mb-4 bg-stone-100">
                                    <img
                                        src={category.image}
                                        alt={category.title}
                                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-stone-900/40 via-transparent to-transparent"></div>
                                    <div className="absolute bottom-4 left-4">
                                        <span className={`px-3 py-1 text-xs font-medium rounded-full ${category.accent}`}>
                                            {category.count}
                                        </span>
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <h4 className="text-lg font-normal text-stone-800">{category.title}</h4>
                                    <div className="w-8 h-px bg-stone-300 group-hover:bg-amber-400 transition-colors duration-300"></div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Craftsmanship Story */}
            <section className="px-6 lg:px-12 py-16 lg:py-20 bg-stone-100">
                <div className="max-w-6xl mx-auto">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
                        <div className="space-y-8">
                            <div className="space-y-4">
                                <h3 className="text-3xl lg:text-4xl font-light text-stone-800 leading-tight">
                                    Handcrafted with<br/>purpose
                                </h3>
                                <p className="text-lg text-stone-600 font-light leading-relaxed">
                                    Our artisans blend traditional Nordic techniques with contemporary design.
                                    Every piece tells a story of heritage, sustainability, and human connection.
                                </p>
                            </div>

                            <div className="grid grid-cols-2 gap-6">
                                <div className="space-y-2">
                                    <div className="text-2xl font-light text-stone-800">15+</div>
                                    <div className="text-sm text-stone-600">Years of craftsmanship</div>
                                </div>
                                <div className="space-y-2">
                                    <div className="text-2xl font-light text-stone-800">100%</div>
                                    <div className="text-sm text-stone-600">Sustainable materials</div>
                                </div>
                                <div className="space-y-2">
                                    <div className="text-2xl font-light text-stone-800">50+</div>
                                    <div className="text-sm text-stone-600">Local artisans</div>
                                </div>
                                <div className="space-y-2">
                                    <div className="text-2xl font-light text-stone-800">Zero</div>
                                    <div className="text-sm text-stone-600">Waste policy</div>
                                </div>
                            </div>
                        </div>

                        <div className="relative">
                            <img
                                src="https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=700&h=500&fit=crop"
                                alt="Nordic Craftsmanship"
                                className="w-full rounded-2xl"
                            />
                            <div className="absolute inset-0 bg-amber-100/10 rounded-2xl"></div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Product Collections with Natural Feel */}
            <section className="px-6 lg:px-12 py-16 lg:py-20 bg-white">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-12 lg:mb-16">
                        <h3 className="text-2xl lg:text-3xl font-light text-stone-800 mb-6">Featured Collections</h3>

                        {/* Natural Filter Tabs */}
                        <div className="flex justify-center gap-6 lg:gap-8">
                            {['All', 'New Arrivals', 'Bestsellers', 'Seasonal'].map((filter) => (
                                <button
                                    key={filter}
                                    onClick={() => setActiveFilter(filter.toLowerCase())}
                                    className={`text-sm font-normal transition-colors duration-300 ${
                                        activeFilter === filter.toLowerCase() || (activeFilter === 'all' && filter === 'All')
                                            ? 'text-amber-700 border-b border-amber-400 pb-1'
                                            : 'text-stone-600 hover:text-stone-800'
                                    }`}
                                >
                                    {filter}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6 lg:gap-8">
                        {Array.from({ length: 20 }, (_, index) => (
                            <div key={index} className="group">
                                <div className="relative aspect-[3/4] overflow-hidden rounded-xl mb-4 bg-stone-100">
                                    <img
                                        src={`https://images.unsplash.com/photo-1515372039744-b8f02a3ae446?w=400&h=500&fit=crop&q=80&auto=format&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&crop=entropy&cs=tinysrgb&fm=jpg&q=80&fit=max&ixlib=rb-4.0.3&ixid=eyJhcHBfaWQiOjF9&auto=format&s=${index * 47 + 2500}`}
                                        alt={`Nordic Item ${index + 1}`}
                                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                                    />
                                    <button className="absolute top-3 right-3 w-9 h-9 bg-white/90 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                                        <Heart size={14} strokeWidth={1.5} className="text-stone-700" />
                                    </button>
                                    {index % 4 === 0 && (
                                        <div className="absolute top-3 left-3 px-2 py-1 bg-amber-100 text-amber-800 text-xs font-medium rounded-full">
                                            New
                                        </div>
                                    )}
                                </div>
                                <div className="space-y-2">
                                    <h4 className="text-sm font-normal text-stone-800 leading-relaxed">Natural Piece {index + 1}</h4>
                                    <p className="text-xs text-stone-500">Sustainable • Handcrafted</p>
                                    <p className="text-base font-medium text-stone-800">${(75 + index * 12)}</p>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="text-center mt-12">
                        <button className="px-8 py-3 border border-stone-300 text-stone-700 font-normal hover:border-stone-800 hover:text-stone-800 transition-all duration-300">
                            View Complete Collection
                        </button>
                    </div>
                </div>
            </section>

            {/* Nordic Video Story */}
            <section className="px-6 lg:px-12 py-16 lg:py-20 bg-gradient-to-b from-stone-50 to-amber-50/30">
                <div className="max-w-5xl mx-auto text-center">
                    <div className="space-y-8">
                        <div className="space-y-4">
                            <h3 className="text-2xl lg:text-4xl font-light text-stone-800">From our workshop to your home</h3>
                            <p className="text-lg text-stone-600 font-light max-w-2xl mx-auto">
                                Take a journey with us through the Nordic landscape and see how tradition meets innovation
                            </p>
                        </div>

                        <div className="relative aspect-video bg-stone-200 rounded-3xl overflow-hidden shadow-lg">
                            <img
                                src="https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=1000&h=600&fit=crop"
                                alt="Nordic Story Video"
                                className="w-full h-full object-cover"
                            />
                            <div className="absolute inset-0 bg-stone-900/20"></div>
                            <button className="absolute inset-0 flex items-center justify-center group">
                                <div className="w-20 h-20 bg-white/95 rounded-full flex items-center justify-center group-hover:bg-white transition-colors duration-300 shadow-lg">
                                    <Play size={24} className="text-stone-800 ml-1" strokeWidth={1.5} />
                                </div>
                            </button>
                        </div>
                    </div>
                </div>
            </section>

            {/* Human Connection */}
            <section className="px-6 lg:px-12 py-16 lg:py-20 bg-white">
                <div className="max-w-4xl mx-auto text-center">
                    <div className="space-y-12">
                        <div className="space-y-6">
                            <h3 className="text-3xl lg:text-4xl font-light text-stone-800 leading-tight">
                                "We design for real life, real people, and real moments"
                            </h3>
                            <div className="flex items-center justify-center gap-3">
                                <div className="w-12 h-12 bg-stone-200 rounded-full"></div>
                                <div className="text-left">
                                    <div className="text-sm font-medium text-stone-800">Erik Lundberg</div>
                                    <div className="text-xs text-stone-500">Creative Director</div>
                                </div>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                            {[
                                { title: "Local Sourcing", desc: "Materials from Nordic suppliers within 200km" },
                                { title: "Fair Wages", desc: "Every person in our supply chain earns a living wage" },
                                { title: "Carbon Neutral", desc: "All shipping and production offset through tree planting" }
                            ].map((principle, index) => (
                                <div key={index} className="text-center space-y-3">
                                    <div className="w-12 h-12 bg-amber-100 rounded-full mx-auto flex items-center justify-center">
                                        <Leaf size={20} className="text-amber-700" strokeWidth={1.5} />
                                    </div>
                                    <h4 className="text-base font-medium text-stone-800">{principle.title}</h4>
                                    <p className="text-sm text-stone-600 font-light leading-relaxed">{principle.desc}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Warm Newsletter */}
            <section className="px-6 lg:px-12 py-16 lg:py-20 bg-amber-50">
                <div className="max-w-2xl mx-auto text-center">
                    <div className="space-y-8">
                        <div className="space-y-4">
                            <h3 className="text-2xl lg:text-3xl font-light text-stone-800">Join our Nordic family</h3>
                            <p className="text-stone-600 font-light">
                                Receive seasonal stories, sustainable tips, and early access to new collections
                            </p>
                        </div>
                        <div className="flex gap-3 max-w-md mx-auto">
                            <input
                                type="email"
                                placeholder="Your email address"
                                className="flex-1 px-6 py-4 bg-white border border-stone-200 rounded-lg text-stone-800 placeholder-stone-400 focus:border-amber-400 focus:outline-none transition-colors duration-300"
                            />
                            <button className="px-8 py-4 bg-stone-800 text-white font-normal rounded-lg hover:bg-stone-900 transition-colors duration-300">
                                Join us
                            </button>
                        </div>
                        <p className="text-xs text-stone-500">We respect your privacy. Unsubscribe at any time.</p>
                    </div>
                </div>
            </section>

            {/* Warm Footer */}
                        {/* Footer */}
            <footer className="bg-gray-900 text-white mt-0">
                <div className="container mx-auto px-4 lg:px-8 py-12 lg:py-16">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-8 lg:gap-12">
                        <div>
                            <h3 className="text-2xl font-bold mb-4 tracking-tight">CURATED</h3>
                            <p className="text-gray-400 text-sm leading-relaxed">Contemporary design excellence.</p>
                        </div>
                        <div>
                            <h4 className="font-semibold mb-4 text-sm uppercase tracking-wide">Quick Links</h4>
                            <ul className="space-y-2 text-sm">
                                <li><span onClick={() => navigate('/template17')} className="text-gray-400 hover:text-white cursor-pointer transition-colors">Home</span></li>
                                <li><span onClick={() => navigate('/template17/shop')} className="text-gray-400 hover:text-white cursor-pointer transition-colors">Shop</span></li>
                                <li><span onClick={() => navigate('/template17/contact')} className="text-gray-400 hover:text-white cursor-pointer transition-colors">Contact</span></li>
                                <li><span onClick={() => navigate('/template17/orders')} className="text-gray-400 hover:text-white cursor-pointer transition-colors">Orders</span></li>
                            </ul>
                        </div>
                        <div>
                            <h4 className="font-semibold mb-4 text-sm uppercase tracking-wide">Customer Service</h4>
                            <ul className="space-y-2 text-sm">
                                <li className="text-gray-400">Shipping & Returns</li>
                                <li className="text-gray-400">Privacy Policy</li>
                                <li className="text-gray-400">Terms & Conditions</li>
                                <li className="text-gray-400">FAQ</li>
                            </ul>
                        </div>
                        <div>
                            <h4 className="font-semibold mb-4 text-sm uppercase tracking-wide">Connect</h4>
                            <p className="text-gray-400 text-sm">hello@curated.com</p>
                        </div>
                    </div>
                    <div className="border-t border-gray-800 mt-12 pt-8">
                        <p className="text-center text-gray-500 text-sm">© 2025 CURATED. All rights reserved.</p>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default Template17;